import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@renderer/chat/providers'
import { getChatApiRoute } from '@shared/utils/ChatBackendUtils'
import type { StagedActionEditOptions } from './types'
import { STAGED_ACTION_EDIT_OPTIONS_QUERY_KEY } from '../../constants'

interface UseStagedActionEditOptionsOptions {
  actionId: string | undefined
}

export function useStagedActionEditOptions({ actionId }: UseStagedActionEditOptionsOptions) {
  const { getAuthToken } = useAuth()

  const {
    data: editOptions,
    isLoading,
    error,
  } = useQuery({
    queryKey: [STAGED_ACTION_EDIT_OPTIONS_QUERY_KEY, actionId],
    queryFn: async () => {
      const token = await getAuthToken()
      const params = new URLSearchParams({
        include: 'editOptions',
        supportedTagTypes: 'select,multiSelect,multiText,localFile,dateTime',
      })
      const response = await fetch(getChatApiRoute(`api/v1/staged-actions/${actionId}?${params.toString()}`), {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!response.ok) throw new Error('Failed to fetch staged action edit options')
      const json = await response.json()
      return (json.data?.editOptions as StagedActionEditOptions) ?? null
    },
    enabled: !!actionId,
    staleTime: 5 * 60 * 1000,
    retry: 2,
  })

  return {
    editOptions: editOptions ?? null,
    isLoading,
    error,
  }
}
